import { baseApi } from './baseApi';
import { ServiceTypes } from '../constants';
import { BlockFromApi, BlockScriptFromApi, BlockStylesheetFromApi } from '../interfaces';

export const mapBlockStylesheetToStore = (item: BlockStylesheetFromApi) => ({
  id: item.ID,
  displayName: item.DisplayName,
  css: item.Css,
});

export const mapBlockScriptToStore = (item: BlockScriptFromApi) => ({
  id: item.ID,
  displayName: item.DisplayName,
  js: item.Js,
});

export const mapBlockToStore = (item: BlockFromApi) => ({
  id: item.ID,
  name: item.Name,
  displayName: item.DisplayName,
  html: item.Html,
  blockStylesheet: item.BlockStylesheet ? mapBlockStylesheetToStore(item.BlockStylesheet) : null,
  blockScript: item.BlockScript ? mapBlockScriptToStore(item.BlockScript) : null,
  schema: item.Schema,
  type: item.Type,
  variables: item.Variables,
  createdAt: item.CreatedAt,
  updatedAt: item.UpdatedAt,
  deletedAt: item.DeletedAt,
});

export type Block = ReturnType<typeof mapBlockToStore>;
export type BlockStylesheet = ReturnType<typeof mapBlockStylesheetToStore>;
export type BlockScript = ReturnType<typeof mapBlockScriptToStore>;

export const micrositeTemplatesService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMicrositeTemplateBlocks: builder.query<any[], void>({
      query: () => `/blocks`,
      transformResponse: (result: { data: BlockFromApi[] }) => result.data.map(mapBlockToStore),
      providesTags: () => [{ type: ServiceTypes.MICROSITE_TEMPLATE, id: 'BLOCKS' }],
    }),
    getMicrositeTemplateBlockById: builder.query<any, { blockId: number }>({
      query: ({ blockId }) => `/blocks/${blockId}`,
      transformResponse: (result: { data: BlockFromApi }) => mapBlockToStore(result.data),
      providesTags: (result, error, { blockId }) => [{ type: ServiceTypes.MICROSITE_TEMPLATE, id: 'BLOCKS' }],
    }),
    createMicrositeTemplateBlock: builder.mutation<
      any,
      {
        data: {
          html: string;
          name: string;
          display_name: string;
          block_stylesheet_id?: number;
          block_script_id?: number;
          variables?: Record<string, any>;
        };
      }
    >({
      query: ({ data }) => ({
        url: `/blocks`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (result: { data: BlockFromApi }) => mapBlockToStore(result.data),
      invalidatesTags: () => [{ type: ServiceTypes.CCMS }, { type: ServiceTypes.MICROSITE_TEMPLATE, id: 'BLOCKS' }],
    }),
    updateMicrositeTemplateBlock: builder.mutation<
      Block,
      {
        blockId: number;
        data: {
          html?: string;
          name?: string;
          display_name?: string;
          block_stylesheet_id?: number;
          block_script_id?: number;
          variables?: Record<string, any>;
        };
      }
    >({
      query: ({ blockId, data }) => ({
        url: `/blocks/${blockId}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (result: { data: BlockFromApi }) => mapBlockToStore(result.data),
      invalidatesTags: () => [{ type: ServiceTypes.CCMS }, { type: ServiceTypes.MICROSITE_TEMPLATE, id: 'BLOCKS' }],
    }),
    deleteMicrositeTemplateBlock: builder.mutation<void, { blockId: number }>({
      query: ({ blockId }) => ({
        url: `/blocks/${blockId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: ServiceTypes.CCMS }],
    }),

    createMicrositeTemplateBlockStylesheet: builder.mutation<
      BlockStylesheet,
      { data: { display_name: string; css: string } }
    >({
      query: ({ data }) => ({
        url: `/block-stylesheets`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (result: { data: BlockStylesheetFromApi }) => mapBlockStylesheetToStore(result.data),
      invalidatesTags: () => [{ type: ServiceTypes.CCMS }, { type: ServiceTypes.MICROSITE_TEMPLATE, id: 'BLOCKS' }],
    }),
    updateMicrositeTemplateBlockStylesheet: builder.mutation<
      BlockStylesheet,
      { stylesheetId: number; data: { display_name?: string; css?: string } }
    >({
      query: ({ stylesheetId, data }) => ({
        url: `/block-stylesheets/${stylesheetId}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (result: { data: BlockStylesheetFromApi }) => mapBlockStylesheetToStore(result.data),
      invalidatesTags: () => [{ type: ServiceTypes.CCMS }, { type: ServiceTypes.MICROSITE_TEMPLATE, id: 'BLOCKS' }],
    }),
    deleteMicrositeTemplateBlockStylesheet: builder.mutation<void, { stylesheetId: number }>({
      query: ({ stylesheetId }) => ({
        url: `/block-stylesheets/${stylesheetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: ServiceTypes.CCMS }, { type: ServiceTypes.MICROSITE_TEMPLATE, id: 'BLOCKS' }],
    }),

    getMicrositeTemplateScriptById: builder.query<any, { scriptId: number }>({
      query: ({ scriptId }) => `/scripts/${scriptId}`,
      transformResponse: (result: IApiResponse<any[]>) => result.data,
      providesTags: (result, error, { scriptId }) => [{ type: ServiceTypes.MICROSITE_TEMPLATE, id: 'SCRIPTS' }],
    }),
    createMicrositeTemplateScript: builder.mutation<BlockScript, { data: { display_name: string; js: string } }>({
      query: ({ data }) => ({
        url: `/scripts`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (result: IApiResponse<BlockScriptFromApi>) => mapBlockScriptToStore(result.data),
      invalidatesTags: () => [{ type: ServiceTypes.CCMS }, { type: ServiceTypes.MICROSITE_TEMPLATE, id: 'THEMES' }],
    }),
    updateMicrositeTemplateScript: builder.mutation<void, { scriptId: number; data: any }>({
      query: ({ scriptId, data }) => ({
        url: `/scripts/${scriptId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: () => [{ type: ServiceTypes.CCMS }, { type: ServiceTypes.MICROSITE_TEMPLATE, id: 'THEMES' }],
    }),
    deleteMicrositeTemplateScript: builder.mutation<void, { scriptId: number }>({
      query: ({ scriptId }) => ({
        url: `/scripts/${scriptId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: ServiceTypes.CCMS }, { type: ServiceTypes.MICROSITE_TEMPLATE, id: 'THEMES' }],
    }),
  }),
});

export const {
  useGetMicrositeTemplateBlocksQuery,
  useGetMicrositeTemplateBlockByIdQuery,
  useCreateMicrositeTemplateBlockMutation,
  useUpdateMicrositeTemplateBlockMutation,
  useDeleteMicrositeTemplateBlockMutation,

  useCreateMicrositeTemplateBlockStylesheetMutation,
  useUpdateMicrositeTemplateBlockStylesheetMutation,
  useDeleteMicrositeTemplateBlockStylesheetMutation,

  useGetMicrositeTemplateScriptByIdQuery,
  useCreateMicrositeTemplateScriptMutation,
  useUpdateMicrositeTemplateScriptMutation,
  useDeleteMicrositeTemplateScriptMutation,
} = micrositeTemplatesService;
